export interface RecordingSessionForTimestampMatch {
  id: string | number;
  launchStartDate: string;
  launchEndDate: string;
}

export interface NoRecordingBannerState {
  key: number;
  message: string;
}

const NO_RECORDING_MESSAGE =
  'No session recording exists for this timestamp. Please contact your administrator.';

let noRecordingBannerKeySeq = 0;

/**
 * React `key` must be unique across rapid clicks; `Date.now()` can collide within a millisecond.
 */
function nextNoRecordingBannerKey(): number {
  noRecordingBannerKeySeq =
    (noRecordingBannerKeySeq + 1) % Number.MAX_SAFE_INTEGER;

  return noRecordingBannerKeySeq;
}

/**
 * Find the best recording session index for a clicked timestamp.
 *
 * Rules:
 * - Prefer a session whose [start, end] range contains the timestamp (inclusive).
 *   If multiple overlap, choose the one with the latest start.
 * - Otherwise, return -1 (caller can show UX message).
 *
 * Returns -1 when sessions is empty or timestamp is invalid.
 */
export function findRecordingIndexForTimestamp(params: {
  clickedMs: number;
  sessions: RecordingSessionForTimestampMatch[];
}): number {
  const { clickedMs, sessions } = params;

  if (!Number.isFinite(clickedMs) || sessions.length === 0) {
    return -1;
  }

  // Rule 1: In-range match.
  let inRangeIdx = -1;
  let bestInRangeStart = -Infinity;

  sessions.forEach((s, idx) => {
    const startMs = Date.parse(s.launchStartDate);
    const endMs = Date.parse(s.launchEndDate);

    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return;
    }

    if (
      startMs <= clickedMs &&
      clickedMs <= endMs &&
      startMs > bestInRangeStart
    ) {
      bestInRangeStart = startMs;
      inRangeIdx = idx;
    }
  });

  if (inRangeIdx >= 0) {
    return inRangeIdx;
  }

  return -1;
}

/**
 * Shared handler for timestamp-driven recording selection (timeline timestamp click,
 * detail panel "Log Start Time (UTC)" click, etc).
 *
 * - If a matching recording is found, selects it, switches to player view, and opens the recording panel.
 * - If no match is found (but sessions exist), shows the standard informational banner.
 */
export function handleRecordingTimestampSelection(params: {
  timestampIso: string | null | undefined;
  sessions: RecordingSessionForTimestampMatch[];
  localize: (msg: string) => string;
  setNoRecordingBanner: (next: NoRecordingBannerState | null) => void;
  setRecordingSelectedIndex: (idx: number) => void;
  setIsRecordingPlayerView: (next: boolean) => void;
  openRecordingPanel: (selectedIdx: number) => void;
}): number | null {
  const {
    timestampIso,
    sessions,
    localize,
    setNoRecordingBanner,
    setRecordingSelectedIndex,
    setIsRecordingPlayerView,
    openRecordingPanel,
  } = params;

  const clickedMs = Date.parse(String(timestampIso ?? ''));

  if (!Number.isFinite(clickedMs)) {
    return null;
  }

  const nextIndex = findRecordingIndexForTimestamp({
    clickedMs,
    sessions,
  });

  if (nextIndex < 0) {
    setNoRecordingBanner({
      key: nextNoRecordingBannerKey(),
      message: localize(NO_RECORDING_MESSAGE),
    });

    return null;
  }

  if (nextIndex >= sessions.length) {
    return null;
  }

  const startMs = Date.parse(
    String(sessions[nextIndex]?.launchStartDate ?? '')
  );
  const seekSeconds =
    Number.isFinite(startMs) && startMs <= clickedMs
      ? Math.max(0, (clickedMs - startMs) / 1000)
      : null;

  setRecordingSelectedIndex(nextIndex);
  setIsRecordingPlayerView(true);
  openRecordingPanel(nextIndex);

  return seekSeconds;
}
